// ============================================================
// tickets.jsx — TravelExpo Egypt
// Cinematic · Luxury · Futuristic · Dark Mode  [FIXED + ENHANCED]
// ============================================================

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { supabase } from '../supabase';
import Nav from '../components/layout/nav';          // ← Nav imported
import './tickets.css';

// ─── Image imports ────────────────────────────────────────────
import heroimg1   from '../assets/option2.png';
import infoimg1   from '../assets/eventsbg.jpg';
import infoimg2   from '../assets/bg22.jpg';
import infoimg3   from '../assets/bg33.jpg';
import infoimg4   from '../assets/bg44.jpg';
import infoimg5   from '../assets/sec3img.png';
import ticketimg1 from '../assets/sunrise.jpg'
import ticketimg2 from '../assets/luxor.jpg';
import ticketimg3 from '../assets/redsea.jpg';
import ticketimg4 from '../assets/eventbg1.jpg';

gsap.registerPlugin(ScrollTrigger);

// ─── Mock events ─────────────────────────────────────────────
const MOCK_EVENTS = [
  {
    id: 1,
    name: 'Pyramids of Giza — Sunrise Gala',
    location: 'Giza Plateau, Cairo',
    date: 'March 14–16, 2025',
    category: 'Heritage',
    description:
      'Witness the last surviving wonder of the ancient world bathed in golden dawn light. An exclusive dawn-to-dusk cultural immersion with private Egyptologist guides, helicopter flybys and a candlelit banquet beneath the stars.',
    image: ticketimg1,
    tickets: [
      { id: 101, type: 'Standard', price: 850,  currency: 'EGP', featured: false,
        benefits: ['General admission', 'Welcome drink', 'Exhibition catalogue', 'Ground shuttle'] },
      { id: 102, type: 'VIP',      price: 2200, currency: 'EGP', featured: true,
        benefits: ['Priority access', 'Private guide', 'Sunset gala dinner', 'Lounge access', 'Gift bag'] },
      { id: 103, type: 'Platinum', price: 5500, currency: 'EGP', featured: false,
        benefits: ['Helicopter arrival', "Chef's table dinner", 'Overnight desert camp', 'Exclusive tour'] },
    ],
  },
  {
    id: 2,
    name: 'Luxor — Temples of Eternity',
    location: 'East Bank, Luxor',
    date: 'April 5–7, 2025',
    category: 'Archaeology',
    description:
      "Explore the world's greatest open-air museum with curated expert-led tours through Karnak and Luxor temples. Exclusive night illuminations, Nile dinner cruises and bespoke accommodation in historic palatial villas.",
    image: ticketimg2,
    tickets: [
      { id: 201, type: 'Standard', price: 1100, currency: 'EGP', featured: false,
        benefits: ['Guided temple tour', 'Audio device', 'Welcome dinner', 'Museum entry'] },
      { id: 202, type: 'VIP',      price: 2900, currency: 'EGP', featured: true,
        benefits: ['All-inclusive Nile cruise', 'Egyptologist guide', 'Night illumination', 'Photography pass'] },
      { id: 203, type: 'Platinum', price: 7000, currency: 'EGP', featured: false,
        benefits: ['Private villa stay', 'Balloon at sunrise', "Chef's menu", 'Exclusive access'] },
    ],
  },
  {
    id: 3,
    name: 'Red Sea — Riviera Summit',
    location: 'El Gouna, Red Sea',
    date: 'May 20–22, 2025',
    category: 'Luxury Resort',
    description:
      "Where cerulean waters meet white-sand luxury. A travel-industry summit meets resort showcase across El Gouna's marina promenades — featuring yacht parties, coral reef experiences and top-tier hospitality networking.",
    image: ticketimg3,
    tickets: [
      { id: 301, type: 'Standard', price: 950,  currency: 'EGP', featured: false,
        benefits: ['Conference access', 'Pool day pass', 'Welcome cocktail', 'Goodie bag'] },
      { id: 302, type: 'VIP',      price: 2600, currency: 'EGP', featured: true,
        benefits: ['Yacht charter access', 'Dive experience', 'All meals included', 'VIP networking'] },
      { id: 303, type: 'Platinum', price: 6200, currency: 'EGP', featured: false,
        benefits: ['Private villa 3 nights', 'Helicopter transfer', 'Exclusive reef tour', 'Spa credits'] },
    ],
  },
  {
    id: 4,
    name: 'Cairo — Sky Exhibition',
    location: 'Cairo Tower, Zamalek',
    date: 'June 8–10, 2025',
    category: 'Aviation & Tourism',
    description:
      'High above the city of a thousand minarets, the most prestigious aviation and travel exhibition in North Africa unfolds across multiple floors of the iconic Cairo Tower — with live flight simulators, airline pavilions and an unmissable rooftop gala.',
    image: ticketimg4,
    tickets: [
      { id: 401, type: 'Standard', price: 750,  currency: 'EGP', featured: false,
        benefits: ['3-day expo pass', 'Flight simulator slot', 'Networking lunch', 'Show programme'] },
      { id: 402, type: 'VIP',      price: 1950, currency: 'EGP', featured: true,
        benefits: ['Rooftop gala invite', 'Airline lounge access', 'Simulator priority', 'Press kit'] },
      { id: 403, type: 'Platinum', price: 4800, currency: 'EGP', featured: false,
        benefits: ['Investor table', 'Private briefings', 'VIP transfers', 'Full concierge'] },
    ],
  },
];

const INFO_CARDS = [
  { tag: 'Heritage',   title: 'Ancient Wonders',  desc: 'Seven thousand years of civilisation woven into every stone, artefact and landscape across the Nile Valley.', image: infoimg1 },
  { tag: 'Luxury',     title: 'Premium Resorts',  desc: "From Red Sea riviera retreats to Nile-side palace hotels — Egypt's hospitality is redefining ultra-luxury travel.", image: infoimg2 },
  { tag: 'Aviation',   title: 'Airline Network',  desc: "Cairo International stands among Africa's most connected hubs, linking Egypt to 80+ destinations worldwide.", image: infoimg3 },
  { tag: 'Innovation', title: 'Travel Tech',      desc: "Smart tourism, digital ticketing, AR guides and sustainable travel — Egypt leads the region into travel's future.", image: infoimg4 },
];

const MARQUEE_ITEMS = [
  'Pyramids of Giza', 'Nile Cruises', 'Luxor Temples', 'Red Sea Riviera',
  'Cairo Skyline', 'Alexandria Coast', 'Aswan Serenity', 'Siwa Oasis',
];

// ─── Scroll reveal ────────────────────────────────────────────
function scrollReveal(target, fromVars, toVars, triggerEl, opts = {}) {
  return gsap.fromTo(target, fromVars, {
    ...toVars,
    scrollTrigger: {
      trigger: triggerEl || target,
      start: opts.start || 'top 88%',
      toggleActions: 'play none none none',
      ...opts,
    },
  });
}

// ============================================================
// PARTICLES
// ============================================================
function Particles({ canvasRef }) {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId;
    let particles = [];

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const createParticles = () => {
      particles = [];
      const count = Math.floor((canvas.width * canvas.height) / 8000);
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: Math.random() * 1.8 + 0.2,
          vx: (Math.random() - 0.5) * 0.25,
          vy: (Math.random() - 0.5) * 0.15 - 0.05,
          a: Math.random(),
          maxA: Math.random() * 0.7 + 0.1,
          fade: Math.random() > 0.5 ? 0.005 : -0.005,
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.a += p.fade;
        if (p.a <= 0 || p.a >= p.maxA) p.fade *= -1;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(214,194,168,${Math.max(0, Math.min(1, p.a))})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };

    resize(); createParticles(); draw();
    const onResize = () => { resize(); createParticles(); };
    window.addEventListener('resize', onResize);
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', onResize); };
  }, [canvasRef]);

  return null;
}

// ============================================================
// PURCHASE MODAL
// ============================================================
function PurchaseModal({ isOpen, onClose, ticketData }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  return (
    <div
      ref={overlayRef}
      className={`modal-overlay${isOpen ? ' modal--open' : ''}`}
      onClick={e => { if (e.target === overlayRef.current) onClose(); }}
      aria-modal="true"
      role="dialog"
    >
      <div className="modal-backdrop" />
      <div className="modal-box">
        <div className="modal-glow" />
        <div className="modal-header">
          <div className="modal-icon">
            <span>🏛</span>
            <div className="modal-icon-ring" />
          </div>
          <h5 className="modal-title">Ready to Confirm?</h5>
          <p className="modal-subtitle">
            You're one step away from securing your place at Egypt's most prestigious travel experience.
          </p>
        </div>

        {ticketData && (
          <div className="modal-ticket-info">
            <div className="modal-ticket-info-cell">
              <span className="modal-ticket-info-label">Ticket Type</span>
              <span className="modal-ticket-info-value">{ticketData.type}</span>
            </div>
            <div className="modal-ticket-info-divider" />
            <div className="modal-ticket-info-cell">
              <span className="modal-ticket-info-label">Price</span>
              <span className="modal-ticket-info-value">
                {ticketData.currency} {ticketData.price?.toLocaleString()}
              </span>
            </div>
            <div className="modal-ticket-info-divider" />
            <div className="modal-ticket-info-cell">
              <span className="modal-ticket-info-label">Event</span>
              <span className="modal-ticket-info-value modal-ticket-info-value--sm">
                {ticketData.event}
              </span>
            </div>
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={onClose}>
            <span>Continue to Purchase</span>
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TICKET CARD
// ============================================================
function TicketCard({ ticket, eventName, onPurchase, index }) {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width  - 0.5;
    const y = (e.clientY - rect.top)  / rect.height - 0.5;
    gsap.to(el, { rotateY: x * 14, rotateX: -y * 14, duration: 0.35, ease: 'power2.out', transformPerspective: 700 });
  };

  const handleMouseLeave = () => {
    gsap.to(cardRef.current, { rotateY: 0, rotateX: 0, duration: 0.7, ease: 'elastic.out(1,0.4)' });
  };

  return (
    <div
      ref={cardRef}
      className={`ticket-card${ticket.featured ? ' ticket-card--featured' : ''}`}
      style={{ '--delay': `${index * 0.12}s` }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {ticket.featured && <span className="ticket-card__badge">Most Popular</span>}
      <div className="ticket-card__top-bar" />
      <span className="ticket-card__type">{ticket.type}</span>
      <div className="ticket-card__price">
        <span className="ticket-card__price-currency">{ticket.currency || 'EGP'}</span>
        <span className="ticket-card__price-amount">{ticket.price?.toLocaleString()}</span>
      </div>
      <div className="ticket-card__divider" />
      <ul className="ticket-card__benefits">
        {(ticket.benefits || []).map((b, i) => (
          <li key={i}>
            <span className="ticket-card__benefit-icon">✦</span>
            {b}
          </li>
        ))}
      </ul>
      <button className="btn-purchase" onClick={() => onPurchase({ ...ticket, event: eventName })}>
        <span className="btn-purchase__text">Purchase Tickets</span>
        <span className="btn-purchase__arrow">→</span>
      </button>
      <div className="ticket-card__corner ticket-card__corner--tl" />
      <div className="ticket-card__corner ticket-card__corner--br" />
    </div>
  );
}

// ============================================================
// CAROUSEL SLIDE
// ============================================================
function CarouselSlide({ event, isActive }) {
  const [modalOpen, setModalOpen]     = useState(false);
  const [modalTicket, setModalTicket] = useState(null);
  const slideRef  = useRef(null);
  const prevActive = useRef(false);

  const handlePurchase = useCallback((data) => {
    setModalTicket(data);
    setModalOpen(true);
  }, []);

  useEffect(() => {
    if (!isActive || prevActive.current || !slideRef.current) return;
    prevActive.current = true;
    const el = slideRef.current;
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    const media  = el.querySelector('.slide__media');
    const info   = el.querySelector('.slide__event-info');
    const label  = el.querySelector('.slide__tickets-label');
    const tcards = el.querySelectorAll('.ticket-card');

    if (media)  tl.fromTo(media,  { clipPath: 'inset(0 100% 0 0)', opacity: 0 }, { clipPath: 'inset(0 0% 0 0)', opacity: 1, duration: 0.9, ease: 'power3.out' }, 0.05);
    if (info)   tl.fromTo(info,   { x: 50, opacity: 0, filter: 'blur(10px)' },   { x: 0, opacity: 1, filter: 'blur(0px)', duration: 0.8 }, 0.25);
    if (label)  tl.fromTo(label,  { x: 30, opacity: 0 },                          { x: 0, opacity: 1, duration: 0.6 }, 0.55);
    if (tcards.length) tl.fromTo(tcards, { y: 36, opacity: 0, scale: 0.94 }, { y: 0, opacity: 1, scale: 1, stagger: 0.1, duration: 0.65, ease: 'back.out(1.5)' }, 0.6);
  }, [isActive]);

  useEffect(() => {
    if (isActive) return;
    prevActive.current = false;
  }, [isActive]);

  return (
    <>
      <div className="carousel__slide" ref={slideRef}>
        <div className="slide-inner">

          {/* Left: image */}
          <div className="slide__media">
            <img src={event.image || ticketimg1} alt={event.name} className="slide__media-img" />
            <div className="slide__media-overlay" />
            <div className="slide__media-meta">
              <span className="slide__category">{event.category}</span>
              <h5 className="slide__event-title">{event.name}</h5>
              <span className="slide__location">
                <span className="slide__location-dot">◎</span>
                {event.location}
              </span>
            </div>
            <div className="slide__media-corner slide__media-corner--tl" />
            <div className="slide__media-corner slide__media-corner--br" />
          </div>

          {/* Right: details + tickets */}
          <div className="slide__details">
            <div className="slide__event-info">
              <div className="slide__event-eyebrow">
                <span className="slide__event-index">{String(event.id ?? 1).padStart(2, '0')}</span>
                <span className="slide__event-line" />
                <span className="slide__event-cat">{event.category}</span>
              </div>
              <h5 className="slide__event-name">{event.name}</h5>
              <div className="slide__event-meta-row">
                <span className="slide__event-date">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="4" width="18" height="18" rx="2"/>
                    <line x1="16" y1="2" x2="16" y2="6"/>
                    <line x1="8" y1="2" x2="8" y2="6"/>
                    <line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  {event.date}
                </span>
                <span className="slide__event-loc-tag">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  {event.location}
                </span>
              </div>
              <p className="slide__event-desc">{event.description}</p>
            </div>

            <div className="slide__tickets-label">
              <span>Select Your Ticket</span>
              <span className="slide__tickets-count">{event.tickets?.length ?? 3} options available</span>
            </div>

            <div className="ticket-cards">
              {(event.tickets || []).map((ticket, idx) => (
                <TicketCard
                  key={ticket.id}
                  ticket={ticket}
                  eventName={event.name}
                  onPurchase={handlePurchase}
                  index={idx}
                />
              ))}
            </div>
          </div>

        </div>
      </div>

      <PurchaseModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        ticketData={modalTicket}
      />
    </>
  );
}

// ============================================================
// INFO CARD (with 3-D tilt)
// ============================================================
function InfoCard({ card, index }) {
  const cardRef = useRef(null);

  const handleMouseMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width  - 0.5;
    const y = (e.clientY - rect.top)  / rect.height - 0.5;
    gsap.to(el, { rotateY: x * 9, rotateX: -y * 9, duration: 0.35, ease: 'power2.out', transformPerspective: 900 });
  };

  const handleMouseLeave = () => {
    gsap.to(cardRef.current, { rotateY: 0, rotateX: 0, duration: 0.7, ease: 'elastic.out(1,0.5)' });
  };

  return (
    <div
      ref={cardRef}
      className="info-card"
      style={{ '--i': index }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="info-card__img-wrap">
        <img src={card.image} alt={card.title} className="info-card__image" />
        <div className="info-card__img-overlay" />
      </div>
      <div className="info-card__body">
        <span className="info-card__tag">{card.tag}</span>
        <h5 className="info-card__title">{card.title}</h5>
        <p className="info-card__desc">{card.desc}</p>
        <div className="info-card__footer">
          <span className="info-card__learn">Discover more</span>
          <div className="info-card__arrow">↗</div>
        </div>
      </div>
      <div className="info-card__glow" />
    </div>
  );
}

// ============================================================
// MAIN PAGE
// ============================================================
export default function Tickets() {
  const pageRef        = useRef(null);
  const heroBgRef      = useRef(null);
  const heroContentRef = useRef(null);
  const eyebrowRef     = useRef(null);
  const titleLine1Ref  = useRef(null);
  const titleLine2Ref  = useRef(null);
  const titleLine3Ref  = useRef(null);
  const subtitleRef    = useRef(null);
  const ctaRef         = useRef(null);
  const scrollRef      = useRef(null);
  const canvasRef      = useRef(null);
  const floatRingRef   = useRef(null);
  const infoIntroRef   = useRef(null);
  const statsRef       = useRef(null);
  const cardsRef       = useRef(null);
  const splitRef       = useRef(null);
  const ticketSecRef   = useRef(null);
  const trackRef       = useRef(null);
  const marqueeRef     = useRef(null);

  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);

  // ─── Fetch ────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: eventsData, error: eventsError } = await supabase
          .from('events').select('*').order('date', { ascending: true });
        if (eventsError) throw eventsError;

        const { data: ticketsData, error: ticketsError } = await supabase
          .from('tickets').select('*');
        if (ticketsError) throw ticketsError;

        const imgMap = [ticketimg1, ticketimg2, ticketimg3, ticketimg4];
        const mapped = eventsData.map((ev, i) => ({
          ...ev,
          image: imgMap[i % imgMap.length],
          tickets: ticketsData.filter(t => t.event_id === ev.id),
        }));

        setEvents(mapped.length ? mapped : MOCK_EVENTS);
      } catch {
        setEvents(MOCK_EVENTS);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ─── Hero entrance ────────────────────────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

      tl.fromTo(heroBgRef.current,
        { scale: 1.18, filter: 'blur(20px)', opacity: 0.3 },
        { scale: 1.05, filter: 'blur(0px)', opacity: 1, duration: 2.6 }, 0);

      tl.fromTo('.hero__hline',
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, stagger: 0.15, duration: 1.2, ease: 'power2.inOut' }, 0.3);

      tl.fromTo('.hero__vline',
        { scaleY: 0, opacity: 0 },
        { scaleY: 1, opacity: 1, stagger: 0.15, duration: 1.2, ease: 'power2.inOut' }, 0.5);

      tl.fromTo(eyebrowRef.current,
        { y: 30, opacity: 0, filter: 'blur(8px)' },
        { y: 0,  opacity: 1, filter: 'blur(0px)', duration: 1.0 }, 0.6);

      [titleLine1Ref, titleLine2Ref, titleLine3Ref].forEach((r, i) => {
        if (!r.current) return;
        tl.fromTo(r.current,
          { yPercent: 115, skewX: 6 },
          { yPercent: 0, skewX: 0, duration: 1.3, ease: 'power4.out' },
          0.7 + i * 0.15);
      });

      tl.fromTo(subtitleRef.current,
        { y: 24, opacity: 0, filter: 'blur(6px)' },
        { y: 0,  opacity: 1, filter: 'blur(0px)', duration: 0.9 }, 1.2);

      tl.fromTo(ctaRef.current,
        { y: 20, opacity: 0 },
        { y: 0,  opacity: 1, duration: 0.8 }, 1.4);

      tl.fromTo('.hero__stat-pill',
        { x: 40, opacity: 0, scale: 0.85 },
        { x: 0,  opacity: 1, scale: 1, stagger: 0.2, duration: 0.7, ease: 'back.out(1.6)' }, 1.6);

      tl.fromTo(scrollRef.current,
        { opacity: 0, y: 14 },
        { opacity: 1, y: 0, duration: 0.6 }, 1.9);

      tl.fromTo(floatRingRef.current,
        { scale: 0.5, opacity: 0, rotate: -120 },
        { scale: 1, opacity: 1, rotate: 0, duration: 1.4, ease: 'back.out(1.3)' }, 1.0);

    }, pageRef);
    return () => ctx.revert();
  }, []);

  // ─── Hero parallax ────────────────────────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.to(heroBgRef.current, {
        yPercent: 28, ease: 'none',
        scrollTrigger: { trigger: '.hero', start: 'top top', end: 'bottom top', scrub: 1.2 },
      });
      gsap.to(heroContentRef.current, {
        yPercent: 22, opacity: 0.1, ease: 'none',
        scrollTrigger: { trigger: '.hero', start: 'center top', end: 'bottom top', scrub: 1 },
      });
    }, pageRef);
    return () => ctx.revert();
  }, []);

  // ─── Scroll reveal animations ─────────────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {

      // Marquee scroll-driven
      if (marqueeRef.current) {
        gsap.to(marqueeRef.current.querySelector('.marquee-track'), {
          xPercent: -33.33, ease: 'none',
          scrollTrigger: { trigger: marqueeRef.current, start: 'top bottom', end: 'bottom top', scrub: 2 },
        });
      }

      // Info intro
      if (infoIntroRef.current) {
        scrollReveal(
          Array.from(infoIntroRef.current.children),
          { y: 65, opacity: 0, filter: 'blur(10px)' },
          { y: 0,  opacity: 1, filter: 'blur(0px)', stagger: 0.22, duration: 1.0, ease: 'power3.out' },
          infoIntroRef.current, { start: 'top 86%' }
        );
      }

      // Stat count-up
      if (statsRef.current) {
        const items = [
          { el: statsRef.current.querySelectorAll('.info__stat-number')[0], end: 80, suffix: '+' },
          { el: statsRef.current.querySelectorAll('.info__stat-number')[1], end: 12, suffix: 'K' },
          { el: statsRef.current.querySelectorAll('.info__stat-number')[2], end: 30, suffix: '+' },
          { el: statsRef.current.querySelectorAll('.info__stat-number')[3], end: 6,  suffix: ''  },
        ];
        items.forEach(({ el, end, suffix }) => {
          if (!el) return;
          const obj = { val: 0 };
          scrollReveal(obj, { val: 0 }, {
            val: end, duration: 2.0, ease: 'power2.out',
            onUpdate: function () { if (el) el.textContent = Math.round(this.targets()[0].val) + suffix; },
          }, statsRef.current, { start: 'top 82%' });
        });
      }

      // Cards reveal
      if (cardsRef.current) {
        Array.from(cardsRef.current.querySelectorAll('.info-card')).forEach((card, i) => {
          scrollReveal(
            card,
            { y: 70, opacity: 0, scale: 0.94, clipPath: 'inset(0 0 25% 0)' },
            { y: 0,  opacity: 1, scale: 1,    clipPath: 'inset(0 0 0% 0)', duration: 0.95, ease: 'power3.out', delay: i * 0.09 },
            cardsRef.current, { start: 'top 86%' }
          );
        });
      }

      // Split section
      if (splitRef.current) {
        const [mediaEl, contentEl] = Array.from(splitRef.current.children);
        scrollReveal(
          mediaEl,
          { x: -90, opacity: 0, clipPath: 'inset(0 35% 0 0)' },
          { x: 0,   opacity: 1, clipPath: 'inset(0 0% 0 0)', duration: 1.1, ease: 'power3.out' },
          splitRef.current, { start: 'top 80%' }
        );
        scrollReveal(
          contentEl,
          { x: 90, opacity: 0, filter: 'blur(8px)' },
          { x: 0,  opacity: 1, filter: 'blur(0px)', duration: 1.1, ease: 'power3.out', delay: 0.12 },
          splitRef.current, { start: 'top 80%' }
        );
        scrollReveal(
          Array.from(contentEl.querySelectorAll('.info__feature-list li')),
          { x: 30, opacity: 0 },
          { x: 0,  opacity: 1, stagger: 0.09, duration: 0.55, ease: 'power2.out', delay: 0.6 },
          splitRef.current, { start: 'top 78%' }
        );
      }

      // Tickets header
      if (ticketSecRef.current) {
        const hdr = ticketSecRef.current.querySelector('.tickets-section__header');
        if (hdr) {
          scrollReveal(
            hdr,
            { y: 50, opacity: 0, filter: 'blur(6px)' },
            { y: 0,  opacity: 1, filter: 'blur(0px)', duration: 1.0, ease: 'power3.out' },
            hdr, { start: 'top 86%' }
          );
        }
      }

    }, pageRef);
    return () => ctx.revert();
  }, []);

  // ─── Carousel GSAP transition ────────────────────────────
  useEffect(() => {
    if (!trackRef.current) return;
    gsap.to(trackRef.current, {
      x: `-${current * 100}%`,
      duration: 1.0,
      ease: 'power3.inOut',
    });
  }, [current]);

  // ─── Badge ring spin ─────────────────────────────────────
  useEffect(() => {
    if (!floatRingRef.current) return;
    gsap.to(floatRingRef.current, { rotate: 360, duration: 24, ease: 'none', repeat: -1 });
  }, []);

  const goNext = () => { if (current < events.length - 1) setCurrent(c => c + 1); };
  const goPrev = () => { if (current > 0) setCurrent(c => c - 1); };

  const displayEvents = events.length ? events : MOCK_EVENTS;

  return (

    <>
      <Nav />

    <div className="tickets-page" ref={pageRef}>




      <Particles canvasRef={canvasRef} />

      {/* ═══════════════════ HERO ════════════════════════════ */}
      <section className="hero">
        <div className="hero__bg" ref={heroBgRef}
          style={{ backgroundImage: `url(${heroimg1})` }} />
        <div className="hero__grain" />
        <canvas className="hero__particles" ref={canvasRef} />
        <div className="hero__orb hero__orb--1" />
        <div className="hero__orb hero__orb--2" />
        <div className="hero__orb hero__orb--3" />

        {/* Decorative lines */}
        <div className="hero__hline hero__hline--top" />
        <div className="hero__hline hero__hline--bottom" />
        <div className="hero__vline hero__vline--left" />
        <div className="hero__vline hero__vline--right" />

        <div className="hero__content" ref={heroContentRef}>
          <span className="hero__eyebrow" ref={eyebrowRef}>
            Egypt Travel Exhibition · 2025
          </span>

          <h1 className="hero__title">
            <span className="hero__title-line">
              <span className="hero__title-inner" ref={titleLine1Ref}>EXPO</span>
            </span>
            <span className="hero__title-line hero__title-accent">
              <span className="hero__title-inner" ref={titleLine2Ref}>TICKETS</span>
            </span>
            <span className="hero__title-line">
              <span className="hero__title-inner" ref={titleLine3Ref}>2026</span>
            </span>
          </h1>

          <p className="hero__subtitle" ref={subtitleRef}>
            North Africa's most immersive travel, tourism and aviation exhibition.
            Book your place at history's greatest stage.
          </p>

          <div className="hero__cta-group" ref={ctaRef}>
            <button className="btn-primary"
              onClick={() => document.querySelector('.tickets-section')?.scrollIntoView({ behavior: 'smooth' })}>
              <span>Explore Events</span>
            </button>
            <button className="btn-ghost"
              onClick={() => document.querySelector('.info')?.scrollIntoView({ behavior: 'smooth' })}>
              Learn More
            </button>
          </div>
        </div>

        {/* Floating stat pills */}
        <div className="hero__stat-pill hero__stat-pill--1 float-anim">
          <span className="hero__stat-pill-num">80+</span>
          <span className="hero__stat-pill-label">Exhibitors</span>
        </div>
        <div className="hero__stat-pill hero__stat-pill--2 float-anim" style={{ animationDelay: '1.4s' }}>
          <span className="hero__stat-pill-num">12K</span>
          <span className="hero__stat-pill-label">Attendees</span>
        </div>

        <div className="hero__scroll" ref={scrollRef}>
          <span className="hero__scroll-text">Scroll</span>
          <div className="hero__scroll-line" />
        </div>

        <div className="hero__badge">
          <div className="hero__badge-ring" ref={floatRingRef}>
            <svg viewBox="0 0 110 110" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <path id="badge-circle"
                  d="M 55,55 m -40,0 a 40,40 0 1,1 80,0 a 40,40 0 1,1 -80,0" />
              </defs>
              <text fill="rgba(214,194,168,0.75)" fontSize="8.5" letterSpacing="3.5" fontFamily="serif">
                <textPath href="#badge-circle">TRAVEL EXPO · EGYPT · 2025 · TRAVEL EXPO ·</textPath>
              </text>
            </svg>
          </div>
          <div className="hero__badge-center">✦<br />2025</div>
        </div>
      </section>

      {/* ─── Marquee ──────────────────────────────────────── */}
      <div className="marquee-strip" ref={marqueeRef} aria-hidden="true">
        <div className="marquee-track">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="marquee-item">
              {item}<span className="marquee-dot">✦</span>
            </span>
          ))}
        </div>
      </div>

      {/* ═══════════════════ INFO ════════════════════════════ */}
      <section className="info">
        <div className="info__intro" ref={infoIntroRef}>
          <div className="info__intro-left">
            <div className="section-label">What is TravelExpo</div>
            <h2>
              Egypt,<br />
              Rediscovered<br />
              <em>Anew</em>
            </h2>
            <div className="info__stats" ref={statsRef}>
              {[
                { num: '80+', label: 'Exhibitors' },
                { num: '12K', label: 'Attendees'  },
                { num: '30+', label: 'Airlines'   },
                { num: '6',   label: 'Destinations' },
              ].map((s, i) => (
                <div className="info__stat-item" key={i}>
                  <span className="info__stat-number">{s.num}</span>
                  <span className="info__stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="info__intro-right">
            <p>
              TravelExpo is Egypt's premier platform for tourism, aviation, luxury hospitality
              and travel innovation — connecting global travellers, industry leaders and
              cultural visionaries across Egypt's most iconic destinations.
            </p>
            <p style={{ marginTop: '16px' }}>
              From the Pyramids to the Red Sea, from Cairo's skyline to Luxor's temples —
              every experience here is engineered to inspire your next journey.
            </p>
            <div className="info__intro-tags">
              {['Heritage', 'Aviation', 'Resorts', 'Cuisine', 'Culture', 'Innovation'].map(t => (
                <span className="info__tag" key={t}>{t}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Cards */}
        <div className="info__cards" ref={cardsRef}>
          <div className="info-card info-card--wide">
            <div className="info-card__img-wrap">
              <img src={infoimg1} alt="Ancient Wonders" className="info-card__image" />
              <div className="info-card__img-overlay" />
            </div>
            <div className="info-card__body">
              <span className="info-card__tag">Heritage & Culture</span>
              <h5 className="info-card__title">Seven Thousand Years of Wonder</h5>
              <p className="info-card__desc">
                From Pharaonic temples to Islamic Cairo, Egypt's heritage is unmatched.
                TravelExpo brings you insider access to experiences most travellers never see.
              </p>
              <div className="info-card__footer">
                <span className="info-card__learn">Discover more</span>
                <div className="info-card__arrow">↗</div>
              </div>
            </div>
            <div className="info-card__glow" />
          </div>

          {INFO_CARDS.slice(1).map((card, i) => (
            <InfoCard key={i} card={card} index={i + 1} />
          ))}
        </div>

        {/* Split */}
        <div className="info__split" ref={splitRef}>
          <div className="info__split-media">
            <img src={infoimg5} alt="Luxury Egypt" />
            <div className="info__split-media-badge">Est. 2020 · Cairo</div>
            <div className="info__split-media-overlay" />
          </div>
          <div className="info__split-content">
            <div className="section-label">The Experience</div>
            <h3>Where Luxury Meets<br />Ancient Legacy</h3>
            <p>
              TravelExpo isn't just an exhibition — it's a gateway. A curated journey
              through Egypt's most extraordinary destinations, experienced through
              the lens of modern luxury and cultural depth.
            </p>
            <p style={{ marginTop: '14px' }}>
              We partner with Egypt's finest resorts, airlines, tour operators and
              cultural institutions to create an unparalleled showcase for travellers
              and industry professionals alike.
            </p>
            <ul className="info__feature-list">
              {[
                'Live cultural performances and immersive installations',
                'Exclusive airline partnerships and lounge experiences',
                'Five-star hospitality and gourmet Egyptian cuisine',
                'Expert-led Egyptology panels and heritage tours',
                'Sustainable tourism innovations and green travel',
              ].map((item, i) => (
                <li key={i}><span className="info__feature-dot" />{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ═══════════════════ TICKETS ═════════════════════════ */}
      <section className="tickets-section" ref={ticketSecRef}>
        <div className="tickets-section__bg-ring tickets-section__bg-ring--1" />
        <div className="tickets-section__bg-ring tickets-section__bg-ring--2" />

        <div className="tickets-section__header">
          <div className="tickets-section__header-left">
            <div className="section-label">Book Your Place</div>
            <h2>Upcoming<br />Events</h2>
          </div>
          <div className="tickets-section__counter" aria-hidden="true">
            {String(displayEvents.length).padStart(2, '0')}
          </div>
        </div>

        {loading ? (
          <div className="tickets-loading">
            <div className="tickets-loading__spinner" />
            <p>Loading Events</p>
          </div>
        ) : (
          <>
            {/* ── Carousel ── */}
            <div className="carousel">
              <div className="carousel__track-wrapper">
                <div
                  className="carousel__track"
                  ref={trackRef}
                >
                  {displayEvents.map((event, idx) => (
                    <CarouselSlide
                      key={event.id}
                      event={event}
                      isActive={idx === current}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* ── Controls ── */}
            <div className="carousel__controls">
              <div className="carousel__nav">
                <button
                  className="carousel__arrow carousel__arrow--prev"
                  onClick={goPrev}
                  disabled={current === 0}
                  aria-label="Previous event"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="19" y1="12" x2="5" y2="12" />
                    <polyline points="12 19 5 12 12 5" />
                  </svg>
                </button>
                <button
                  className="carousel__arrow carousel__arrow--next"
                  onClick={goNext}
                  disabled={current === displayEvents.length - 1}
                  aria-label="Next event"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                </button>
              </div>

              <div className="carousel__progress-track">
                <div
                  className="carousel__progress-fill"
                  style={{ width: `${((current + 1) / displayEvents.length) * 100}%` }}
                />
              </div>

              <div className="carousel__dots">
                {displayEvents.map((_, i) => (
                  <button
                    key={i}
                    className={`carousel__dot${i === current ? ' carousel__dot--active' : ''}`}
                    onClick={() => setCurrent(i)}
                    aria-label={`Event ${i + 1}`}
                  />
                ))}
              </div>

              <div className="carousel__count">
                <span className="carousel__count-cur">{String(current + 1).padStart(2, '0')}</span>
                <span className="carousel__count-sep"> / </span>
                <span className="carousel__count-total">{String(displayEvents.length).padStart(2, '0')}</span>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
    </>
  );
}